import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useRef, useImperativeHandle, forwardRef, useCallback, useEffect } from 'react';
import { Table, useLocalizeMessage, SearchField, Toggle, Select, } from 'libs.nucleus';
import { DATA_FIELD_TYPE_OPTIONS, NO_DATA_FORMAT, getDataFormatOptions, isDataFormatDisabled, isValidDataFormatForField, } from '../../constants/data_view.constants';
const VisibilityToggle = ({ defaultValue, onChange, disabled = false, dataTestId, }) => {
    const [checked, setChecked] = useState(defaultValue);
    const handleChange = useCallback((value) => {
        setChecked(value);
        onChange(value);
    }, [onChange]);
    return _jsx(Toggle, { checked: checked, onChange: handleChange, disabled: disabled, dataTestId: dataTestId });
};
export const DataSettings = forwardRef(({ items, onItemsChange, isReadOnly = false }, ref) => {
    const translate = useLocalizeMessage();
    const [searchQuery, setSearchQuery] = useState('');
    // Store changes in a ref to avoid re-renders - only read on flush
    const changesRef = useRef(new Map());
    // Data type and data format live in their own columns (SSP-3918), but they remain dependent:
    // the format options are derived from the type, and changing the type resets a now-invalid
    // format. Since the two columns render as separate cells, this shared state keeps them in sync.
    const buildTypeFormatState = useCallback(() => Object.fromEntries(items.map((item) => [item.id, { type: item.type, format: item.format || NO_DATA_FORMAT }])), [items]);
    const [typeFormatById, setTypeFormatById] = useState(buildTypeFormatState);
    // Resync if the underlying items change (e.g. modal reopened on a different dataset).
    useEffect(() => {
        setTypeFormatById(buildTypeFormatState());
    }, [buildTypeFormatState]);
    // Expose flush function to parent via ref
    useImperativeHandle(ref, () => ({
        flushPendingChanges: () => {
            // Apply all accumulated changes and notify parent
            if (changesRef.current.size === 0) {
                onItemsChange(items);
                return;
            }
            const updatedItems = items.map((item) => {
                const changes = changesRef.current.get(item.id);
                if (changes) {
                    return { ...item, ...changes };
                }
                return item;
            });
            changesRef.current.clear();
            onItemsChange(updatedItems);
        },
    }));
    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) {
            return items;
        }
        const query = searchQuery.toLowerCase();
        return items.filter((item) => item.name.toLowerCase().includes(query) ||
            item.description.toLowerCase().includes(query) ||
            item.category.toLowerCase().includes(query));
    }, [items, searchQuery]);
    // Handle changes by storing in ref (no state updates, no re-renders)
    const handleFieldChange = useCallback((id, field, value) => {
        const existing = changesRef.current.get(id) || {};
        changesRef.current.set(id, { ...existing, [field]: value });
    }, []);
    const typeOptions = useMemo(() => DATA_FIELD_TYPE_OPTIONS.map(({ labelKey, value }) => ({ label: translate(labelKey), value })), [translate]);
    const handleTypeChange = useCallback((item, newType) => {
        const current = typeFormatById[item.id] ?? { type: item.type, format: item.format || NO_DATA_FORMAT };
        // Reset to "None" when the current format is not valid for the newly selected type.
        const resetFormat = !isValidDataFormatForField(item.category, newType, current.format);
        handleFieldChange(item.id, 'type', newType);
        if (resetFormat) {
            handleFieldChange(item.id, 'format', NO_DATA_FORMAT);
        }
        setTypeFormatById((prev) => ({
            ...prev,
            [item.id]: { type: newType, format: resetFormat ? NO_DATA_FORMAT : current.format },
        }));
    }, [typeFormatById, handleFieldChange]);
    const handleFormatChange = useCallback((item, newFormat) => {
        handleFieldChange(item.id, 'format', newFormat);
        setTypeFormatById((prev) => ({
            ...prev,
            [item.id]: { type: prev[item.id]?.type ?? item.type, format: newFormat },
        }));
    }, [handleFieldChange]);
    const columns = useMemo(() => [
        {
            header: translate('Internal name'),
            accessorKey: 'name',
            size: 240,
            cell: ({ row }) => _jsx("div", { className: 'py-2 font-medium', children: row.original.name }),
        },
        {
            header: translate('Display name'),
            accessorKey: 'displayName',
            size: 220,
            cell: ({ row }) => (_jsx("input", { type: 'text', id: `displayName-${row.original.id}`, defaultValue: row.original.displayName || '', onChange: (e) => handleFieldChange(row.original.id, 'displayName', e.target.value), disabled: isReadOnly, className: 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-text-secondary disabled:cursor-not-allowed', autoComplete: 'off' })),
        },
        {
            header: translate('Description'),
            accessorKey: 'description',
            size: 260,
            cell: ({ row }) => (_jsx("input", { type: 'text', id: `description-${row.original.id}`, defaultValue: row.original.description || '', onChange: (e) => handleFieldChange(row.original.id, 'description', e.target.value), disabled: isReadOnly, className: 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-text-secondary disabled:cursor-not-allowed', autoComplete: 'off' })),
        },
        {
            header: translate('Data type'),
            accessorKey: 'type',
            size: 170,
            cell: ({ row }) => {
                const item = row.original;
                const type = typeFormatById[item.id]?.type ?? item.type;
                return (_jsx(Select, { dataTestId: `field-data-type-${item.name}`, options: typeOptions, value: typeOptions.find((option) => option.value === type), onChange: (option) => handleTypeChange(item, option.value), disabled: isReadOnly, width: 'full' }));
            },
        },
        {
            header: translate('Data format'),
            accessorKey: 'format',
            size: 170,
            cell: ({ row }) => {
                const item = row.original;
                const type = typeFormatById[item.id]?.type ?? item.type;
                const format = typeFormatById[item.id]?.format ?? (item.format || NO_DATA_FORMAT);
                const formatOptions = getDataFormatOptions(item.category, type).map(({ labelKey, value, descriptionKey }) => ({
                    label: translate(labelKey),
                    value,
                    ...(descriptionKey ? { description: translate(descriptionKey) } : {}),
                }));
                const selectedFormat = formatOptions.find((option) => option.value === format) ?? formatOptions[0];
                return (_jsx(Select, { dataTestId: `field-data-format-${item.name}`, options: formatOptions, value: selectedFormat, onChange: (option) => handleFormatChange(item, option.value), disabled: isReadOnly || isDataFormatDisabled(item.category, type), width: 'full' }));
            },
        },
        {
            header: translate('Is visible'),
            accessorKey: 'isVisible',
            size: 90,
            cell: ({ row }) => (_jsx(VisibilityToggle, { defaultValue: row.original.isVisible, onChange: (checked) => handleFieldChange(row.original.id, 'isVisible', checked), disabled: isReadOnly, dataTestId: `field-visibility-${row.original.name}` })),
        },
        {
            header: translate('Category'),
            accessorKey: 'category',
            size: 110,
            cell: ({ row }) => _jsx("div", { className: 'py-2', children: row.original.category }),
        },
    ], [translate, isReadOnly, typeOptions, typeFormatById, handleFieldChange, handleTypeChange, handleFormatChange]);
    return (_jsxs("div", { className: 'flex flex-col size-full gap-4', children: [_jsx("div", { className: 'font-semibold text-lg', children: translate('Configure each dimension and measure') }), _jsx("div", { className: 'max-w-md', children: _jsx(SearchField, { ariaLabel: translate('Search dimensions and measures'), placeholder: translate('Search...'), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), onClear: () => setSearchQuery('') }) }), _jsx("div", { "data-testid": 'data-settings-table-scroll', className: 'flex-1 min-h-0 [&_.nucleus-overflow-x-auto]:max-h-[60vh] [&_.nucleus-overflow-x-auto]:overflow-y-auto [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20', children: _jsx(Table, { data: filteredItems, columns: columns, labelNoResults: translate('No items found'), enableSorting: false, getRowId: (row) => row.id }) })] }));
});
DataSettings.displayName = 'DataSettings';
